import type React from 'react';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BadgeCheck, MapPin, Calendar, ExternalLink, Globe, Music,
  Hand, Handshake, Ban, Users, ChevronDown, ChevronUp
} from 'lucide-react';
import performerApi from '@showliner/shared/services/performerApi';
import { connectionApi } from '@showliner/shared/services/connectionApi';
import { useUser } from '@showliner/shared/context/UserContext';
import { useManagedEntity } from '@showliner/shared/context/ManagedEntityContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { getAssetUrl, getAdminUrl } from '@/utils/crossAppLinks';
import { renderSocialIcon, getPlatformColor, type SocialPlatformKey, getPlatformLabel, type SocialLinkEntry, getVisibleSocialLinks } from '@showliner/shared/utils/socialMedia';
import type { ProfileLink, UpcomingEventsLink } from '@showliner/shared/types/profileLinks';
import { toast } from 'sonner';
import { usePageTitle } from '@/hooks/usePageTitle';
import ProfileContact, { type PublicContact } from '@/components/ProfileContact';
import PlayedTogether from '@/components/PlayedTogether';
import { browserTimeZone, formatListedShowTime } from '@showliner/shared/utils/showTimezone';

interface PerformerData {
  /** Published contact (email/phone), or null. Never the direct tier. */
  publicContact?: PublicContact | null;
  id: string;
  name: string;
  bio?: string | null;
  profilePicture?: string | null;
  socialLinks?: SocialLinkEntry[] | null;
  customLinks?: ProfileLink[] | null;
  isVerified?: boolean;
  managedEntityId?: string | null;
  parentEntityId?: string | null;
  genres?: string[];
  upcomingEvents?: Array<{
    id: string;
    name: string;
    startTime: string;
    /** The show's IANA zone (#863); null when none is on record. */
    timezone?: string | null;
    venueName?: string;
  }>;
}

// --- Social icon link ---

const SocialIconLink: React.FC<{ platform: string; url: string }> = ({ platform, url }) => {
  const platformKey = platform.toLowerCase() as SocialPlatformKey;
  const icon = renderSocialIcon(platformKey, { size: 20 });
  const color = getPlatformColor(platformKey);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={getPlatformLabel(platform)}
      className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-border/50 hover:scale-110 transition-all duration-200"
      style={{ color }}
    >
      {icon || <Globe className="h-5 w-5" />}
    </a>
  );
};

// --- Upcoming Events block ---

const UpcomingEventsBlock: React.FC<{
  link: UpcomingEventsLink;
  events?: PerformerData['upcomingEvents'];
}> = ({ link, events }) => {
  const [expanded, setExpanded] = useState(false);

  if (!events || events.length === 0) return null;

  const displayMode = link.displayMode || 'next_three';
  // The visitor's zone, only to decide whether to name the show's (#863).
  const viewerZone = browserTimeZone();
  let visibleEvents = events;
  let canExpand = false;

  if (displayMode === 'next' && !expanded) {
    visibleEvents = events.slice(0, 1);
    canExpand = events.length > 1;
  } else if (displayMode === 'next_three' && !expanded) {
    visibleEvents = events.slice(0, 3);
    canExpand = events.length > 3;
  }

  if (expanded) {
    visibleEvents = events;
  }

  return (
    <div className="w-full rounded-lg border border-border/50 overflow-hidden">
      <div className="px-4 py-3 bg-muted/30 border-b border-border/50">
        <h3 className="text-sm font-semibold text-grey-900">{link.title || 'Upcoming shows'}</h3>
      </div>
      <div className="divide-y divide-border/30">
        {visibleEvents.map((event) => (
          <div key={event.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-grey-900 truncate">{event.name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{formatListedShowTime(event.startTime, event.timezone, viewerZone)}</span>
                {event.venueName && (
                  <>
                    <span>&middot;</span>
                    <span className="flex items-center gap-0.5">
                      <MapPin className="h-3 w-3" />
                      {event.venueName}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-2.5 text-xs font-medium text-primary hover:bg-muted/20 transition-colors flex items-center justify-center gap-1"
        >
          {expanded ? (
            <><ChevronUp className="h-3.5 w-3.5" /> Show less</>
          ) : (
            <><ChevronDown className="h-3.5 w-3.5" /> Show all {events.length} events</>
          )}
        </button>
      )}
    </div>
  );
};

// --- Profile link button ---

const ProfileLinkButton: React.FC<{ title: string; url: string }> = ({ title, url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="w-full flex items-center justify-between px-5 py-3.5 rounded-lg border border-border/50 bg-white hover:border-primary/30 hover:shadow-sm transition-all duration-200 group"
  >
    <span className="text-sm font-medium text-grey-900">{title}</span>
    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
  </a>
);

// --- Loading skeleton ---

const ProfileSkeleton: React.FC = () => (
  <div className="flex flex-col items-center px-4 pt-12 pb-8 max-w-lg mx-auto">
    <Skeleton className="h-28 w-28 rounded-full" />
    <Skeleton className="h-7 w-48 mt-4" />
    <Skeleton className="h-4 w-64 mt-2" />
    <div className="flex gap-3 mt-6">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-10 rounded-full" />)}
    </div>
    <div className="w-full space-y-3 mt-8">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
    </div>
  </div>
);

// --- Main component ---

const PerformerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const entityContext = useManagedEntity();
  const managedEntity = entityContext?.currentEntity;
  const [performer, setPerformer] = useState<PerformerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCollaborating, setIsCollaborating] = useState(false);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  usePageTitle(performer?.name);

  useEffect(() => {
    const fetchPerformer = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await performerApi.getPerformer(id);
        setPerformer(data as unknown as PerformerData);

        if (managedEntity && data.managedEntityId && managedEntity.id !== data.managedEntityId) {
          // Directional lookup — catches pending + accepted so the button
          // can render "Pending" before the request is accepted.
          const conn = await connectionApi.getConnection(managedEntity.id, data.managedEntityId);
          const hasOutgoing = !!conn && (conn.status === 'pending' || conn.status === 'accepted');
          setIsCollaborating(hasOutgoing);

          if (conn?.status === 'accepted') {
            const hasBidirectional = await connectionApi.areConnected(
              managedEntity.id,
              data.managedEntityId,
            );
            setIsCollaborator(hasBidirectional);
          } else {
            setIsCollaborator(false);
          }
        }
      } catch {
        setError('Couldn\'t load this profile.');
      } finally {
        setLoading(false);
      }
    };

    fetchPerformer();
  }, [id, managedEntity]);

  const handleCollaboration = async () => {
    if (!managedEntity || !performer?.managedEntityId) {
      toast.error('Please sign in to collaborate');
      return;
    }

    try {
      if (isCollaborating) {
        await connectionApi.disconnect(managedEntity.id, performer.managedEntityId);
        setIsCollaborating(false);
        setIsCollaborator(false);
        toast.success('Collaboration removed');
      } else {
        await connectionApi.requestConnection(managedEntity.id, performer.managedEntityId);
        setIsCollaborating(true);
        toast.success('Collaboration request sent');
      }
    } catch {
      toast.error('That didn\'t go through. Try again.');
    }
  };

  if (loading) return <ProfileSkeleton />;

  if (error || !performer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Music className="h-16 w-16 text-muted-foreground/50" />
        <p className="text-lg text-muted-foreground">{error || 'Performer not found'}</p>
        <Button variant="outline" asChild>
          <Link to="/discover">Browse performers</Link>
        </Button>
      </div>
    );
  }

  const isLocalPerformer = managedEntity && performer.parentEntityId === managedEntity.id;
  const visibleSocialLinks = getVisibleSocialLinks(performer.socialLinks || []);
  const hasSocialLinks = visibleSocialLinks.length > 0;
  const customLinks = (performer.customLinks || []).filter(l => l.visible !== false).sort((a, b) => a.position - b.position);

  return (
    <div className="flex flex-col items-center px-4 pt-12 pb-16 max-w-lg mx-auto">
      {/* Avatar */}
      <Avatar className="h-28 w-28 shadow-lg">
        {performer.profilePicture ? (
          <AvatarImage src={getAssetUrl(performer.profilePicture)} alt={performer.name} />
        ) : null}
        <AvatarFallback className="text-3xl font-bold bg-primary text-white">
          {performer.name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      {/* Name + Badge */}
      <div className="flex items-center gap-2 mt-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-grey-900">{performer.name}</h1>
        {performer.isVerified && <BadgeCheck className="h-6 w-6 text-verified" />}
      </div>

      {isLocalPerformer && (
        <Badge variant="outline" className="mt-1.5" title="Not on Showliner yet">
          <Users className="mr-1 h-3 w-3" /> Unclaimed
        </Badge>
      )}

      {/* Genres */}
      {performer.genres && performer.genres.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1.5 mt-3">
          {performer.genres.map((genre) => (
            <Badge key={genre} variant="secondary" className="text-xs">
              <Music className="mr-1 h-3 w-3" /> {genre}
            </Badge>
          ))}
        </div>
      )}

      {/* Bio */}
      {performer.bio && (
        <p className="text-sm text-muted-foreground text-center mt-3 max-w-md leading-relaxed">
          {performer.bio}
        </p>
      )}

      {/* Social Links */}
      {hasSocialLinks && (
        <div className="flex flex-wrap justify-center gap-2.5 mt-5">
          {visibleSocialLinks.map((entry) => (
            <SocialIconLink key={entry.platform} platform={entry.platform} url={entry.url} />
          ))}
        </div>
      )}

      {/* Public contact tier, when the workspace published one */}
      <ProfileContact contact={performer.publicContact} />

      {/* Action Buttons */}
      <div className="flex items-center gap-2 mt-5">
        {!isLocalPerformer && managedEntity && (
          <Button
            variant={isCollaborating ? 'outline' : 'default'}
            size="sm"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={handleCollaboration}
          >
            {isHovering && isCollaborating ? (
              <><Ban className="mr-1 h-3.5 w-3.5" /> Remove</>
            ) : isCollaborating ? (
              <><Handshake className="mr-1 h-3.5 w-3.5" /> {isCollaborator ? 'Collaborator' : 'Pending'}</>
            ) : (
              <><Hand className="mr-1 h-3.5 w-3.5" /> Collaborate</>
            )}
          </Button>
        )}
        {managedEntity && (
          <Button variant="outline" size="sm" asChild>
            <a href={getAdminUrl(`/events/create?performerId=${performer.id}`)}>
              <Calendar className="mr-1 h-3.5 w-3.5" /> Create event
            </a>
          </Button>
        )}
        {!user && (
          <Button size="sm" asChild>
            <Link to="/register">Sign up to collaborate</Link>
          </Button>
        )}
      </div>

      {/* Profile Blocks */}
      <div className="w-full space-y-3 mt-8">
        {customLinks.map((link) => {
          if (link.type === 'url') {
            return <ProfileLinkButton key={link.id} title={link.title} url={link.url} />;
          }
          if (link.type === 'upcoming_events') {
            return (
              <UpcomingEventsBlock
                key={link.id}
                link={link}
                events={performer.upcomingEvents}
              />
            );
          }
          return null;
        })}
      </div>

      {/* Who it played with (#41 phase 3) */}
      <div className="w-full mt-3">
        <PlayedTogether entityType="performer" entityId={performer.id} />
      </div>

    </div>
  );
};

export default PerformerProfile;
